<template>
  <div>
    <!-- the four numbers a school actually asks for, in the order it asks -->
    <v-row class="mb-1">
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption text--secondary">{{ $t('coins_left') }}</div>
          <div class="text-h5">{{ balance }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" :color="runningLow ? 'error' : undefined" :dark="runningLow">
          <div class="text-caption" :class="runningLow ? '' : 'text--secondary'">
            {{ $t('lasts_until') }}
          </div>
          <div class="text-h5">{{ burn.runs_out_on || '—' }}</div>
          <div class="text-caption">
            {{ burn.days_left === null ? $t('no_spending_yet') : $t('school_days_left', { days: burn.days_left }) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption text--secondary">{{ $t('spent_in_range') }}</div>
          <div class="text-h5">{{ totals.spent }}</div>
          <div class="text-caption text--secondary">
            {{ $t('over_days', { days: totals.days_with_spend }) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption text--secondary">{{ $t('given_back') }}</div>
          <div class="text-h5">{{ totals.refunded }}</div>
          <div class="text-caption text--secondary">{{ $t('bought_in_range', { coins: totals.bought }) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-cash-clock</v-icon>
        {{ $t('coin_ledger') }}
        <v-spacer></v-spacer>
        <v-select
          v-model="range"
          :items="ranges"
          dense
          outlined
          hide-details
          style="max-width: 200px"
          @change="load"
        ></v-select>
      </v-card-title>
      <v-card-subtitle>{{ $t('coin_ledger_hint') }}</v-card-subtitle>

      <v-card-text>
        <vue-element-loading :active="isLoading" />

        <empty-state
          v-if="!isLoading && !days.length"
          icon="mdi-cash-clock"
          :title="$t('no_coin_movement')"
          :body="$t('no_coin_movement_hint')"
        ></empty-state>

        <v-simple-table v-else-if="days.length" dense>
          <template #default>
            <thead>
              <tr>
                <th>{{ $t('date') }}</th>
                <th class="text-end">{{ $t('children') }}</th>
                <th class="text-end">{{ $t('spent') }}</th>
                <th class="text-end">{{ $t('given_back') }}</th>
                <th class="text-end">{{ $t('bought') }}</th>
                <th>{{ $t('reason') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in days" :key="day.date">
                <td class="text-no-wrap">{{ day.date }}</td>
                <td class="text-end">{{ day.children || '' }}</td>
                <td class="text-end">{{ day.spent || '' }}</td>
                <td class="text-end">
                  <span v-if="day.refunded" class="success--text">+{{ day.refunded }}</span>
                </td>
                <td class="text-end">
                  <span v-if="day.bought" class="primary--text">+{{ day.bought }}</span>
                </td>
                <td class="text-caption text--secondary">{{ day.refund_reason }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
/*
 * Where the coins went.
 *
 * A school could see what it had bought and what was left, and nothing in
 * between - and the first time anyone looked at the gap was when the balance
 * hit zero and children started being suspended for it. That is the worst
 * possible moment to be asking what the money was spent on.
 *
 * So: a day at a time, and the date the balance runs out worked out from the
 * days the buses actually ran rather than the days on the calendar, said while
 * there is still time to do something about it.
 */
import VueElementLoading from "vue-element-loading";
import EmptyState from "@/components/EmptyState";

export default {
  name: "CoinsIndex",
  components: { VueElementLoading, EmptyState },
  data() {
    return {
      days: [],
      totals: { spent: 0, refunded: 0, bought: 0, days_with_spend: 0 },
      burn: { per_day: null, days_left: null, runs_out_on: null },
      balance: 0,
      range: 30,
      isLoading: false,
    };
  },
  computed: {
    ranges() {
      return [
        { text: this.$t("last_30_days"), value: 30 },
        { text: this.$t("last_90_days"), value: 90 },
        { text: this.$t("last_year"), value: 365 },
      ];
    },
    //three weeks is about how long a purchase order takes to clear
    runningLow() {
      return this.burn.days_left !== null && this.burn.days_left <= 15;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - (this.range - 1));

      this.isLoading = true;
      axios
        .get("/school-coins/ledger", {
          params: {
            from: from.toISOString().slice(0, 10),
            to: to.toISOString().slice(0, 10),
          },
        })
        .then((response) => {
          this.days = response.data.days || [];
          this.totals = response.data.totals || this.totals;
          this.burn = response.data.burn || this.burn;
          this.balance = response.data.balance || 0;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_ledger"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
